import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import Navbar from "../components/Navbar";
import SEO from "../components/SEO";
import { toast } from "sonner";

// Unsplash supports on-the-fly resizing — switching from full-res hero to 600px webp
// shrinks the gallery payload from ~30MB to ~3MB, slashing load time on mobile data.
function thumb(url) {
  if (!url) return url;
  if (!url.includes("images.unsplash.com")) return url;
  // Strip any pre-existing w=/q= and append our own
  const base = url.split("?")[0];
  return `${base}?w=600&q=65&auto=format&fm=webp&fit=crop`;
}

export default function TemplatesGallery() {
  const [templates, setTemplates] = useState([]);
  const [busy, setBusy] = useState(null);
  const [cfg, setCfg] = useState({ price: 999, mrp: 4999 });
  const { user } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    api.get("/templates").then((r) => setTemplates(r.data));
    api.get("/config/pricing").then((r) => setCfg(r.data)).catch(() => {});
  }, []);

  const choose = async (tmpl) => {
    if (!user) {
      toast("Please sign in to start customizing", { description: "It's free — sign in with Google in one click." });
      return nav(`/signup?next=${encodeURIComponent("/templates")}`);
    }
    setBusy(tmpl.id);
    try {
      const r = await api.post("/invitations", { template_id: tmpl.id });
      toast.success(`${tmpl.name} ready to customize`);
      nav(`/editor/${r.data.id}`);
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "";
      const status = err?.response?.status;
      if (status === 401) {
        toast.error("Session expired. Please sign in again.");
        return nav("/login");
      }
      toast.error(`Could not create invitation${detail ? `: ${detail}` : ""}${status ? ` (${status})` : ""}`);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="templates-gallery">
      <SEO
        title="23 Wedding Invitation Templates · Royal, Floral, Beach, Punjabi & more"
        description="Browse 23 hand-crafted wedding invitation website templates — Royal Indian, Bengali Lotus, Punjabi Phulkari, Christian Chapel, Beach, Floral, Minimalist & more. Free to customize. Live preview before publishing."
        path="/templates"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          "name": "Weddinsite Templates",
          "description": "23 hand-crafted wedding invitation website templates",
          "url": "https://weddinsite.com/templates",
        }}
      />
      <Navbar />
      <div className="px-6 md:px-12 lg:px-20 py-16">
        <div className="text-xs uppercase tracking-[0.4em] text-stone-500 mb-3">The Collection</div>
          <h1 className="font-serif-display text-5xl md:text-7xl font-light tracking-tight max-w-3xl mb-3">{templates.length || 23} templates. Endless moods.</h1>
        <p className="text-stone-600 max-w-2xl">Each design is hand-tuned with bespoke fonts, colors and animations. Design freely — preview, customize, and publish when you&apos;re ready.</p>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {templates.length === 0 && Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="bg-white border border-stone-200 animate-pulse">
              <div className="aspect-[4/5] bg-stone-200" />
              <div className="p-6 space-y-3">
                <div className="h-6 bg-stone-200 w-3/4" />
                <div className="h-4 bg-stone-200 w-1/2" />
              </div>
            </div>
          ))}
          {templates.map((t, i) => (
            <div key={t.id} className="group relative bg-white border border-stone-200 hover:-translate-y-1 hover:shadow-xl transition-all duration-300" data-testid={`template-${t.id}-card`}>
              <div className="aspect-[4/5] overflow-hidden relative bg-stone-100">
                <img
                  src={thumb(t.preview)}
                  alt={t.name}
                  loading={i < 3 ? "eager" : "lazy"}
                  decoding="async"
                  width={600}
                  height={750}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-3" style={{ background: "rgba(10,9,8,0.55)" }}>
                  <a
                    href={`/i/demo-${t.id.replace(/_/g, "-")}`}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`template-${t.id}-preview-live`}
                    className="px-7 py-3 bg-transparent border text-xs uppercase tracking-[0.3em] hover:bg-white/10 transition-colors"
                    style={{ borderColor: "#D4AF37", color: "#F4E5BC" }}
                  >
                    Preview Live →
                  </a>
                  <button
                    data-testid={`template-${t.id}-choose`}
                    disabled={busy === t.id}
                    onClick={() => choose(t)}
                    className="px-7 py-3 text-xs uppercase tracking-[0.3em] font-semibold hover:brightness-110 transition-all"
                    style={{ background: "#D4AF37", color: "#0A0908" }}
                  >
                    {busy === t.id ? "Creating..." : "Use Template →"}
                  </button>
                </div>
                <button onClick={() => choose(t)} data-testid={`template-${t.id}-choose-mobile`} aria-label={`Use ${t.name}`} className="md:hidden absolute bottom-3 right-3 px-3 py-1.5 bg-stone-900/90 backdrop-blur text-white text-[10px] uppercase tracking-[0.2em] rounded-full">
                  {busy === t.id ? "..." : "Use →"}
                </button>
              </div>
              <div className="p-6">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-serif-display text-2xl">{t.name}</div>
                    <div className="text-sm text-stone-500 mt-1">{t.tagline}</div>
                  </div>
                </div>
                <div className="mt-4 flex items-center gap-2">
                  <span className="w-4 h-4 rounded-full border border-stone-200" style={{ background: t.palette.bg }} />
                  <span className="w-4 h-4 rounded-full border border-stone-200" style={{ background: t.palette.accent }} />
                  <span className="w-4 h-4 rounded-full border border-stone-200" style={{ background: t.palette.text }} />
                </div>
                <div className="mt-4 pt-4 border-t border-stone-100">
                  <span
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] rounded-full border"
                    style={{ borderColor: "#C9A84C", color: "#8a6f1a", background: "rgba(201, 168, 76, 0.08)" }}
                    data-testid={`template-${t.id}-price-badge`}
                  >
                    ✦ Free to try <span className="opacity-70">·</span> ₹999 to publish
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
